import { create } from 'zustand';
import { apiClient } from '@/api/client';

export interface Staff {
  id: string;
  name: string;
  role: 'MANAGER' | 'CASHIER' | 'PRODUCTION' | 'VIEWER';
  branchId: string;
  branchName: string;
}

export interface Branch {
  id: string;
  name: string;
  location?: string;
}

interface AuthState {
  staff: Staff | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Staff move between the two shops, so the branch you are working in is
  // not necessarily your home branch. Everything (stock, sales, shifts)
  // follows this, not staff.branchId.
  activeBranchId: string;
  activeBranchName: string;
  branches: Branch[];
  setActiveBranch: (id: string, name: string) => void;
  loadBranches: () => Promise<void>;

  login: (staffId: string, pin: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  validatePin: (pin: string) => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set) => ({
  staff: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  activeBranchId: '',
  activeBranchName: '',
  branches: [],

  setActiveBranch: (id: string, name: string) => {
    localStorage.setItem('active_branch', JSON.stringify({ id, name }));
    set({ activeBranchId: id, activeBranchName: name });
  },

  loadBranches: async () => {
    try {
      const res = await apiClient.getBranches();
      set({ branches: Array.isArray(res.data) ? res.data : [] });
    } catch {
      set({ branches: [] });
    }
  },

  login: async (staffId: string, pin: string) => {
    set({ isLoading: true, error: null });
    try {
      const result = await apiClient.login(staffId, pin);
      // Start in your home branch; the selector can move you to the other one
      const saved = (() => {
        try { return JSON.parse(localStorage.getItem('active_branch') || 'null'); } catch { return null; }
      })();
      set({
        staff: result.staff,
        isAuthenticated: true,
        isLoading: false,
        activeBranchId: saved?.id || result.staff.branchId,
        activeBranchName: saved?.name || result.staff.branchName,
      });
      void (useAuthStore.getState() as any).loadBranches();
    } catch (err: any) {
      set({
        error: err.response?.data?.message || 'Login failed',
        isLoading: false,
      });
      throw err;
    }
  },

  logout: () => {
    apiClient.logout();
    localStorage.removeItem('active_branch');
    set({
      staff: null,
      isAuthenticated: false,
      error: null,
    });
  },

  clearError: () => {
    set({ error: null });
  },

  validatePin: async (pin: string) => {
    try {
      const result = await apiClient.validatePin(pin);
      return result.data.valid;
    } catch {
      return false;
    }
  },
}));
